import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, Button, Container, Spinner, Row, Col } from 'react-bootstrap';
import { FaSave, FaStore } from 'react-icons/fa';
import Swal from 'sweetalert2';
import Navigation from './Navigation';
import useAuth from '../../hooks/useAuth';
import AdminPageHeader from './AdminPageHeader';
import { OUTLET_TYPE_OPTIONS, normalizeOutletTypeValue } from './outletTypeOptions';
import {
    extractNullableString,
    normalizeRestaurantMenuTemplate,
    RESTAURANT_MENU_TEMPLATE_OPTIONS,
    supportsRestaurantMenuTheme,
} from './restaurantMenuTemplateOptions';

function EditOutlet() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { verifyAndRefreshToken } = useAuth();

    const [outletData, setOutletData] = useState({
        outlet_name: '',
        outlet_address: '',
        outlet_phone: '',
        outlet_email: '',
        outlet_sosmed: '',
        username: '',
        password: '',
        status: '',
        parent: '',
        outlet_type: '',  // ✅ Added without modifying form structure
        menu_template: 'classic_warm',
        menu_background: '',
        restaurant_table_count: 12,
        restaurant_tax_percent: 0,
        restaurant_service_percent: 0,
    });

    const [loading, setLoading] = useState(true);
    const [parentOutlets, setParentOutlets] = useState([]); // ✅ Parent Outlets list
    const normalizePercentInput = (value) => Math.min(100, Math.max(0, Number.parseFloat(value) || 0));

    // ✅ Fetch Outlet Data
    const fetchOutletData = useCallback(async () => {
        const token = localStorage.getItem('sessionToken');
        if (!token) {
            navigate('/loginadmin');
            return;
        }

        try {
            const isValid = await verifyAndRefreshToken(token);
            if (!isValid) {
                navigate('/loginadmin');
                return;
            }

            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}getoutlet`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ id: parseInt(id) }),
            });

            const data = await response.json();
            if (data.error === "0" && data.results) {
                setOutletData({
                    outlet_name: data.results.outlet_name || '',
                    outlet_address: data.results.outlet_address || '',
                    outlet_phone: data.results.outlet_phone || '',
                    outlet_email: data.results.outlet_email || '',
                    outlet_sosmed: data.results.outlet_sosmed || '',
                    username: data.results.username || '',
                    password: '',
                    status: data.results.status === 1 ? 'active' : 'inactive',
                    parent: data.results.parent?.toString() || '0',
                    outlet_type: normalizeOutletTypeValue(data.results.outlet_type),
                    menu_template: normalizeRestaurantMenuTemplate(extractNullableString(data.results.menu_template)),
                    menu_background: extractNullableString(data.results.menu_background),
                    restaurant_table_count: Number(data.results.restaurant_table_count || 12) || 12,
                    restaurant_tax_percent: Number(data.results.restaurant_tax_percent || 0) || 0,
                    restaurant_service_percent: Number(data.results.restaurant_service_percent || 0) || 0,
                });
            } else {
                throw new Error(data.message || 'Failed to fetch outlet data');
            }
        } catch (err) {
            Swal.fire('Error', err.message, 'error');
        } finally {
            setLoading(false);
        }
    }, [id, navigate, verifyAndRefreshToken]);

    // ✅ Fetch Parent Outlets for Selection
    const fetchParentOutlets = useCallback(async () => {
        const token = localStorage.getItem('sessionToken');
        if (!token) return;

        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}outletlist`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ limit: 100, start: 0 }),
            });

            const data = await response.json();
            if (data.error === "0") {
                setParentOutlets(data.results);
            } else {
                throw new Error('Failed to fetch parent outlets');
            }
        } catch (err) {
            console.error('Error fetching parent outlets:', err);
        }
    }, []);

    useEffect(() => {
        fetchOutletData();
        fetchParentOutlets();
    }, [fetchOutletData, fetchParentOutlets]);

    // ✅ Handle Submit
    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        setLoading(true);
        const token = localStorage.getItem('sessionToken');

        const requestData = {
            id: parseInt(id),
            outlet_name: outletData.outlet_name,
            outlet_address: outletData.outlet_address,
            outlet_phone: outletData.outlet_phone,
            outlet_email: outletData.outlet_email,
            outlet_sosmed: outletData.outlet_sosmed,
            username: outletData.username,
            password: outletData.password || undefined,
            status: outletData.status === 'active' ? 1 : 0,
            parent: parseInt(outletData.parent) || 0,
            outlet_type: outletData.outlet_type || '',
            menu_template: outletData.menu_template || 'classic_warm',
            menu_background: outletData.menu_background || '',
            restaurant_table_count: Number(outletData.restaurant_table_count || 12) || 12,
            restaurant_tax_percent: normalizePercentInput(outletData.restaurant_tax_percent),
            restaurant_service_percent: normalizePercentInput(outletData.restaurant_service_percent),
        };

        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}editoutlet`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(requestData),
            });

            const data = await response.json();
            if (data.error === "0") {
                await Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: 'Outlet updated successfully',
                    timer: 1500
                });
                navigate('/outlets');
            } else {
                throw new Error(data.message || 'Failed to update outlet');
            }
        } catch (err) {
            Swal.fire('Error', err.message, 'error');
        } finally {
            setLoading(false);
        }
    }, [id, navigate, outletData]);

    if (loading) {
        return (
            <div className="loading-container">
                <Spinner animation="border" variant="primary" />
                <p>Loading outlet data...</p>
            </div>
        );
    }
    return (
        <div>
            <Navigation />
            <Container fluid className="admin-page-shell mt-4 px-4">
                <AdminPageHeader
                    icon={FaStore}
                    eyebrow="Branch administration"
                    title="Update Outlet"
                    description="Perbarui informasi outlet, status operasional, akun akses, dan struktur relasi outlet secara aman."
                    breadcrumbs={[
                        { label: 'Dashboard', to: '/dashboardadmin' },
                        { label: 'Outlet Management', to: '/outlets' },
                        { label: 'Update Outlet' },
                    ]}
                />

                <div className="form-container p-4">
                    <div className="form-header mb-4">
                        <h4 className="mb-3"><FaStore className="me-2" />Edit Outlet</h4>
                        <p className="text-muted border-bottom pb-3">Update outlet information below</p>
                    </div>

                    <Form onSubmit={handleSubmit} className="form-layout">
                        <Row className="g-4">
                            <Col lg={6}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">Outlet Name</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="outlet_name"
                                        value={outletData.outlet_name}
                                        onChange={(e) => setOutletData({...outletData, outlet_name: e.target.value})}
                                        required
                                        className="form-control-lg"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Email</Form.Label>
                                    <Form.Control
                                        type="email"
                                        name="outlet_email"
                                        value={outletData.outlet_email}
                                        onChange={(e) => setOutletData({...outletData, outlet_email: e.target.value})}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Form.Group className="mb-3">
                            <Form.Label>Address</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                name="outlet_address"
                                value={outletData.outlet_address}
                                onChange={(e) => setOutletData({...outletData, outlet_address: e.target.value})}
                                required
                            />
                        </Form.Group>

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Phone</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="outlet_phone"
                                        value={outletData.outlet_phone}
                                        onChange={(e) => setOutletData({...outletData, outlet_phone: e.target.value})}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Social Media</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="outlet_sosmed"
                                        value={outletData.outlet_sosmed}
                                        onChange={(e) => setOutletData({...outletData, outlet_sosmed: e.target.value})}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Username</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="username"
                                        value={outletData.username}
                                        onChange={(e) => setOutletData({...outletData, username: e.target.value})}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Password (leave blank to keep current)</Form.Label>
                                    <Form.Control
                                        type="password"
                                        name="password"
                                        value={outletData.password}
                                        onChange={(e) => setOutletData({...outletData, password: e.target.value})}
                                        placeholder="Enter new password if changing"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Status</Form.Label>
                                    <Form.Select
                                        name="status"
                                        value={outletData.status}
                                        onChange={(e) => setOutletData({...outletData, status: e.target.value})}
                                        required
                                    >
                                        <option value="">Select Status</option>
                                        <option value="active">Active</option>
                                        <option value="inactive">Inactive</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Parent Outlet ID</Form.Label>
                                <Form.Select
                                    name="parent"
                                    value={outletData.parent}
                                    onChange={(e) => setOutletData({ ...outletData, parent: e.target.value })}
                                >
                                    <option value="0">No Parent</option>
                                    {parentOutlets.map(outlet => (
                                        <option key={outlet.id} value={outlet.id}>
                                            {outlet.outlet_name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Outlet Type</Form.Label>
                                <Form.Select
                                    name="outlet_type"
                                    value={outletData.outlet_type}
                                    onChange={(e) => setOutletData({ ...outletData, outlet_type: e.target.value })}
                                    required
                                >
                                    <option value="">Select Outlet Type</option>
                                    {OUTLET_TYPE_OPTIONS.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </Form.Select>
                                <Form.Text>
                                    Tipe outlet menentukan workspace kasir dan pemisahan alur operasional seperti Gym Center, Gym + Store, Restaurant, POS Store, Store Bahan Bangunan, Hybrid, Cafe + Gym, atau Cafe + Store.
                                </Form.Text>
                            </Form.Group>
                            </Col>
                        </Row>
                        {supportsRestaurantMenuTheme(outletData.outlet_type) ? (
                            <Row>
                                <Col lg={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Menu Template</Form.Label>
                                        <Form.Select
                                            name="menu_template"
                                            value={outletData.menu_template}
                                            onChange={(e) => setOutletData({ ...outletData, menu_template: e.target.value })}
                                        >
                                            {RESTAURANT_MENU_TEMPLATE_OPTIONS.map(option => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </Form.Select>
                                        <Form.Text>
                                            Template ini dipakai untuk halaman menu publik outlet.
                                        </Form.Text>
                                    </Form.Group>
                                </Col>
                                <Col lg={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Background Image URL</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="menu_background"
                                            value={outletData.menu_background}
                                            onChange={(e) => setOutletData({ ...outletData, menu_background: e.target.value })}
                                            placeholder="https://..."
                                        />
                                        <Form.Text>
                                            Opsional. Isi URL gambar untuk memberi background berbeda di menu outlet ini.
                                        </Form.Text>
                                    </Form.Group>
                                </Col>
                                <Col lg={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Jumlah Meja Restoran</Form.Label>
                                        <Form.Control
                                            type="number"
                                            min="1"
                                            name="restaurant_table_count"
                                            value={outletData.restaurant_table_count}
                                            onChange={(e) => setOutletData({ ...outletData, restaurant_table_count: e.target.value })}
                                        />
                                        <Form.Text>
                                            Board meja kasir akan mengikuti jumlah meja ini.
                                        </Form.Text>
                                    </Form.Group>
                                </Col>
                                <Col lg={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Tax Persentase (%)</Form.Label>
                                        <Form.Control
                                            type="number"
                                            min="0"
                                            max="100"
                                            step="0.01"
                                            name="restaurant_tax_percent"
                                            value={outletData.restaurant_tax_percent}
                                            onChange={(e) => setOutletData({ ...outletData, restaurant_tax_percent: normalizePercentInput(e.target.value) })}
                                        />
                                        <Form.Text>
                                            Persentase pajak default untuk transaksi POS restaurant outlet ini.
                                        </Form.Text>
                                    </Form.Group>
                                </Col>
                                <Col lg={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Service Persentase (%)</Form.Label>
                                        <Form.Control
                                            type="number"
                                            min="0"
                                            max="100"
                                            step="0.01"
                                            name="restaurant_service_percent"
                                            value={outletData.restaurant_service_percent}
                                            onChange={(e) => setOutletData({ ...outletData, restaurant_service_percent: normalizePercentInput(e.target.value) })}
                                        />
                                        <Form.Text>
                                            Persentase service charge default untuk transaksi POS restaurant outlet ini.
                                        </Form.Text>
                                    </Form.Group>
                                </Col>
                            </Row>
                        ) : null}

                        <div className="d-flex justify-content-end gap-3 mt-5">
                            <Button 
                                variant="outline-secondary" 
                                size="lg"
                                onClick={() => navigate('/outlets')}
                                className="px-4"
                            >
                                Cancel
                            </Button>
                            <Button 
                                variant="primary" 
                                type="submit" 
                                size="lg"
                                disabled={loading}
                                className="px-4"
                            >
                                {loading ? (
                                    <>
                                        <Spinner size="sm" className="me-2" />
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <FaSave className="me-2" />
                                        Save Changes
                                    </>
                                )}
                            </Button>
                        </div>
                    </Form>
                </div>
            </Container>
        </div>
    );
}

export default EditOutlet;
